
// Import React polyfill first to ensure proper initialization
import './react-polyfill';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { Auth0Provider } from '@auth0/auth0-react';
import App from './App.tsx';
import './index.css';
import "@/services/performanceOptimization";
import { Auth0SessionBridge } from '@/components/auth/Auth0SessionBridge';
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, defaultAuthProvider, isAuth0Configured } from '@/config/auth';

// Clerk is legacy (only /clerklogin). Auth0 is default. Publishable keys are
// non-secret and safe to ship in the browser bundle; only pk_test_ / pk_live_
// values are accepted here.
// Fallbacks are embedded because Build Secrets are not accessible for this
// workspace tier. Publishable keys (not secret keys) can safely live in code.
const PUBLISHABLE_KEY_DEV =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_DEV ||
  'pk_test_c2F2ZWQtbWFuYXRlZS0yOC5jbGVyay5hY2NvdW50cy5kZXYk';
const PUBLISHABLE_KEY_PROD =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_PROD ||
  'pk_live_Y2xlcmsuY2Fsb25lLmNvbSQ';
const CLERK_FRONTEND_API_PROD = import.meta.env.VITE_CLERK_FRONTEND_API_PROD;

// Production = events.calone.com or any *.calone.com host. Everything else
// (localhost, Lovable preview domains, staging) uses the development key.
const hostname = window.location.hostname;
const isProduction =
  hostname === 'events.calone.com' || hostname.endsWith('.calone.com');

const PUBLISHABLE_KEY = isProduction ? PUBLISHABLE_KEY_PROD : PUBLISHABLE_KEY_DEV;
const CLERK_FRONTEND_API = isProduction ? CLERK_FRONTEND_API_PROD : undefined;

if (!PUBLISHABLE_KEY) {
  const varName = isProduction
    ? 'VITE_CLERK_PUBLISHABLE_KEY_PROD'
    : 'VITE_CLERK_PUBLISHABLE_KEY_DEV';
  throw new Error(
    `Missing Clerk publishable key: ${varName} is not set. Add it as a Build Secret in Workspace Settings and re-publish.`,
  );
}

if (!/^pk_(test|live)_/.test(PUBLISHABLE_KEY)) {
  throw new Error(
    'Invalid Clerk publishable key: must start with pk_test_ or pk_live_. Do not use secret keys in the browser.',
  );
}

// Import thumbnail utilities for development
if (!isProduction) {
  import('./utils/generateThumbnails').then((module) => {
    // Thumbnail utilities loaded silently
  }).catch(() => {
    // Ignore errors in case the file doesn't exist
  });
}

if (defaultAuthProvider === 'auth0' && !isAuth0Configured) {
  throw new Error('Missing Auth0 configuration: VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID are required when VITE_AUTH_PROVIDER=auth0');
}

// Ensure React is fully loaded before proceeding
if (typeof React === 'undefined') {
  throw new Error('React is not loaded properly');
}

// Ensure DOM is ready
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Add error handling for the root render with proper error boundaries
try {
  const root = createRoot(rootElement);

  const auth0Tree = (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={(appState) => {
        const requestedReturnTo = appState?.returnTo || sessionStorage.getItem('redirect_after_login') || '/dashboard';
        const returnTo = !requestedReturnTo || requestedReturnTo === '/' ? '/dashboard' : requestedReturnTo;
        console.info('[Auth0] redirect callback', {
          appStateReturnTo: appState?.returnTo,
          sessionReturnTo: sessionStorage.getItem('redirect_after_login'),
          returnTo,
        });
        sessionStorage.removeItem('redirect_after_login');
        window.history.replaceState({}, document.title, returnTo);
      }}
    >
      <Auth0SessionBridge />
      <App />
    </Auth0Provider>
  );

  const tree = (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      {...(CLERK_FRONTEND_API && { frontendApi: CLERK_FRONTEND_API })}
      afterSignOutUrl="/"
      appearance={{
        layout: {
          logoImageUrl: "https://v1.calone.com/static/graphics/CalOne_Events_31_Logo.png",
          logoPlacement: 'inside',
          socialButtonsPlacement: 'top',
          socialButtonsVariant: 'blockButton',
          showOptionalFields: true,
          animations: true,
          shimmer: true,
        },
        variables: {
          colorPrimary: "#669933",
          colorText: "#222",
        },
      }}
    >
      {auth0Tree}
    </ClerkProvider>
  );

  root.render(<React.StrictMode>{tree}</React.StrictMode>);
} catch (error) {
  console.error('Failed to initialize app:', error);
  
  // Fallback error display using vanilla DOM
  rootElement.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: system-ui;">
      <div style="text-align: center;">
        <h1 style="color: #dc2626; margin-bottom: 1rem;">Failed to load application</h1>
        <p style="color: #6b7280; margin-bottom: 1rem;">Please try refreshing the page</p>
        <button onclick="window.location.reload()" style="background: #3b82f6; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.375rem; cursor: pointer;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
}
